import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import store from '@/store/store';
import { setAccessToken } from '@/store/slices/authSlice';
import { clearSession } from '@/lib/auth/clearSession';
import {
  transformCamelToSnake,
  transformSnakeToCamel,
  transformQueryParams,
} from '@/utils/caseTransform';

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor - Add auth token and transform case
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // Add auth token
  const token = store.getState().auth.accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Transform request body from camelCase to snake_case
  if (config.data && typeof config.data === 'object') {
    config.data = transformCamelToSnake(config.data);
  }

  // Transform query params from camelCase to snake_case
  if (config.params) {
    config.params = transformQueryParams(config.params);
  }

  return config;
});

// Track token refresh state to prevent multiple simultaneous refreshes
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token!);
    }
  });
  failedQueue = [];
};

// Response interceptor - Transform case and handle 401 token refresh
apiClient.interceptors.response.use(
  (response) => {
    // Transform response data from snake_case to camelCase
    if (response.data) {
      response.data = transformSnakeToCamel(response.data);
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Handle 401 errors with token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(apiClient(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = store.getState().auth.refreshToken;

      // Don't redirect on public routes - just reject the request
      const publicRoutePatterns = ['/restaurants/', '/lists/', '/profile/'];
      const isPublicRoute = publicRoutePatterns.some(p => window.location.pathname.startsWith(p));

      if (!refreshToken) {
        await clearSession({ skipServerDeregister: true });
        // Only redirect to login if not on a public route
        if (!isPublicRoute) {
          window.location.replace('/login');
        }
        return Promise.reject(error);
      }

      try {
        // Call refresh endpoint directly (not through apiClient to avoid interceptor loop)
        const response = await axios.post(
          `${import.meta.env.VITE_API_URL}/auth/token/refresh/`,
          { refresh: refreshToken }
        );

        const newAccessToken = response.data.access;
        store.dispatch(setAccessToken(newAccessToken));
        processQueue(null, newAccessToken);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        await clearSession({ skipServerDeregister: true });
        // Only redirect to login if not on a public route
        if (!isPublicRoute) {
          window.location.replace('/login');
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Generic fetch function for TanStack Query
 * Handles GET, POST, PUT, PATCH, DELETE requests with automatic case transformation
 */
export async function apiFetch<TResponse>(
  url: string,
  options?: {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    body?: unknown;
    params?: Record<string, unknown>;
    /**
     * Cancels the request when aborted. Axios (>=0.22) supports the
     * standard `AbortSignal` natively via its `signal` config option — no
     * separate `CancelToken` plumbing needed. Typically sourced from
     * TanStack Query's `queryFn({ signal })` context so a query that's
     * superseded (params changed, component unmounted) cancels its
     * in-flight request instead of letting a stale response land later.
     */
    signal?: AbortSignal;
  }
): Promise<TResponse> {
  const { method = 'GET', body, params, signal } = options ?? {};
  const response = await apiClient({
    url,
    method,
    data: body,
    params,
    signal,
  });
  return response.data;
}

export default apiClient;
